import { z } from 'zod';
import { Agent, Id, TimestampMs } from './common.js';
/**
 * Additive (v0): history import, terminal-session adoption, artifact storage status and
 * Compare/Pick helpers. See docs/protocol.md "History import and adoption".
 */
const f = (type, shape) => z.object({ type: z.literal(type), ...shape });
/** Max events per `history.import` call (the daemon chunks larger sessions). */
export const HISTORY_IMPORT_MAX_EVENTS = 500;
/** Max serialized bytes of the events in one `history.import` call. */
export const HISTORY_IMPORT_MAX_BYTES = 1024 * 1024;
/**
 * Run metadata for a session that Viko did not launch:
 * - `imported`: a past session read from `~/.claude/projects` / `~/.codex/sessions` (read-only);
 * - `adopted`: a session still being typed in a terminal, streamed live (read-only; "typing
 *   elsewhere"). `live: false` marks the end of adoption (the file went quiet).
 * `runId` is the importer's `claude:<sessionId>` / `codex:<threadId>`.
 */
export const ImportedRunMeta = z.looseObject({
    runId: Id,
    agent: Agent,
    source: z.enum(['imported', 'adopted']),
    nativeSessionId: Id,
    /** Session file on the uploading machine. */
    path: z.string().optional(),
    cwd: z.string().optional(),
    /** Hub project the session maps to (by git root), when known. */
    projectId: z.string().optional(),
    /** Main repo root (`guessProject`); worktrees resolve to their repo. */
    gitRoot: z.string().optional(),
    /** Worktree root when the session ran in a linked worktree. */
    worktree: z.string().optional(),
    gitBranch: z.string().optional(),
    title: z.string().optional(),
    firstPrompt: z.string().optional(),
    model: z.string().optional(),
    cliVersion: z.string().optional(),
    entrypoint: z.string().optional(),
    startedAt: TimestampMs.optional(),
    endedAt: TimestampMs.optional(),
    sizeBytes: z.number().int().nonnegative().optional(),
    /** Earlier session this one continues (Claude resume/continue copies). */
    continuedFrom: z.string().optional(),
    /** Adopted sessions: still being written by a terminal. */
    live: z.boolean().optional(),
});
/**
 * daemon → hub rpc `history.import`: upsert the run's metadata and append a chunk of its events.
 * Events carry the importer's seq (gap-free from 1) and stable eventIds; the hub stores them
 * idempotently (a chunk already stored is a no-op) and answers `{ack, inserted, duplicates}`
 * where `ack` is the highest contiguous stored seq — the daemon's resume cursor.
 */
export const HistoryImportParams = z.object({
    run: ImportedRunMeta,
    events: z.array(z.unknown()).max(HISTORY_IMPORT_MAX_EVENTS),
});
/** hub → daemon: whether artifact storage (R2) accepts uploads. Sent after `welcome` and on change. */
export const StorageStatusFrame = f('storage.status', {
    artifacts: z.boolean(),
});
/** "Copy resume command" for a session outside Viko. */
export function resumeCommandLine(r) {
    const cmd = r.agent === 'codex'
        ? `codex resume ${r.nativeSessionId}`
        : `claude --resume ${r.nativeSessionId}`;
    return r.cwd ? `cd ${shellQuote(r.cwd)} && ${cmd}` : cmd;
}
function shellQuote(s) {
    return `'${s.replace(/'/g, `'\\''`)}'`;
}
// ---- Compare / Pick ------------------------------------------------------------------------------
export const COMPARE_LABELS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
/**
 * Compare card labels: artifacts are grouped by `group` (in order of first appearance; artifacts
 * without a group stand alone). A one-item group gets a letter (`A`), a larger group gets
 * letter + index (`C1`, `C2`).
 */
export function compareLabels(items) {
    const groups = [];
    for (const it of items) {
        const g = it.group ? groups.find((x) => x.key === `g:${it.group}`) : undefined;
        if (g)
            g.ids.push(it.id);
        else
            groups.push({ key: it.group ? `g:${it.group}` : `i:${it.id}`, ids: [it.id] });
    }
    const out = [];
    groups.forEach((g, i) => {
        const letter = i < COMPARE_LABELS.length ? COMPARE_LABELS[i] : `Z${i}`;
        if (g.ids.length === 1)
            out.push({ id: g.ids[0], label: letter });
        else
            for (const [j, id] of g.ids.entries())
                out.push({ id, label: `${letter}${j + 1}` });
    });
    return out;
}
/**
 * Parse a pick reply ("A, A, C1"): tokens split on commas, semicolons or whitespace; a token is a
 * label, or a group letter that picks every item of that group. Order and repeats are kept (a
 * reply can answer several slots).
 */
export function parsePickReply(reply, items) {
    const picks = [];
    const unknown = [];
    for (const raw of reply.split(/[\s,;]+/)) {
        const token = raw.trim().toUpperCase();
        if (!token)
            continue;
        if (!/^[A-Z]{1,3}\d*$/.test(token)) {
            unknown.push(raw.trim());
            continue;
        }
        const exact = items.filter((i) => i.label.toUpperCase() === token);
        const ids = exact.length
            ? exact.map((i) => i.id)
            : items
                .filter((i) => new RegExp(`^${token}\\d+$`).test(i.label.toUpperCase()))
                .map((i) => i.id);
        if (ids.length)
            picks.push({ token, ids });
        else
            unknown.push(raw.trim());
    }
    return { picks, unknown };
}
//# sourceMappingURL=history.js.map